import React, { useState, useEffect } from 'react'
import {
  Button, Card, Input, Select, message, Typography, Switch, Tooltip, Empty, Tag,
} from 'antd'
import {
  PlusOutlined, ApartmentOutlined, MinusCircleOutlined, SaveOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons'
import {
  useMasterData, type WorkflowConfig, type WorkflowStep, type Employee,
} from '@/context/MasterDataContext'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'

const { Text } = Typography

// Every module that can carry an approval chain. This list is fixed — a
// workflow is a setting for a known module, not a free-form record — so the
// page always renders one card per module, creating the row on first save.
const WORKFLOW_MODULES = [
  'Proposal', 'Agreement', 'Invoice', 'Allocation', 'Payment', 'Payout', 'Mapping',
] as const

// A step's default name is the business action it represents, not its ordinal:
// "Proposal Approval" / "Agreement Approval" rather than "Step 1" / "Step 2".
// Later steps in the same chain are levelled so a two-stage chain reads
// "Proposal Approval" → "Proposal Approval — Level 2".
export const defaultStepName = (module: string, index: number): string =>
  index === 0 ? `${module} Approval` : `${module} Approval — Level ${index + 1}`

const MODULE_HINT: Record<string, string> = {
  Proposal:  'Runs when a proposal is sent for approval.',
  Agreement: 'Runs when an agreement is raised for sign-off.',
  Invoice:   'Runs before an invoice is issued to the customer.',
  Allocation:'Runs when a unit allocation is submitted.',
  Payment:   'Runs when a payment record is submitted.',
  Payout:    'Runs when a generator payout is submitted.',
  Mapping:   'Generator–Consumer mapping. Approval is restricted to Admin users regardless of the approver named here.',
}

const blankConfig = (module: string): WorkflowConfig => ({
  id: '',
  module,
  steps: [],
  isActive: false,
  status: 'active',
})

// ── One module's approval chain ─────────────────────────────────────────────
const WorkflowCard: React.FC<{
  config: WorkflowConfig
  employees: Employee[]
  onSave: (cfg: WorkflowConfig) => Promise<void>
}> = ({ config, employees, onSave }) => {
  const [draft, setDraft] = useState<WorkflowConfig>(config)
  const [saving, setSaving] = useState(false)

  // Re-seed when the server copy changes (hydrate lands, or another tab saves).
  useEffect(() => { setDraft(config) }, [config])

  const employeeOptions = employees
    .filter(e => e.status === 'active')
    .map(e => ({ value: e.name, label: `${e.name} (${e.role})` }))

  const dirty = JSON.stringify(draft) !== JSON.stringify(config)

  const update = (patch: Partial<WorkflowConfig>) => setDraft(d => ({ ...d, ...patch }))

  const addStep = () => {
    const newStep: WorkflowStep = {
      stepNo: draft.steps.length + 1,
      stepName: defaultStepName(draft.module, draft.steps.length),
      approverName: '',
      isRequired: true,
      action: 'both',
    }
    update({ steps: [...draft.steps, newStep] })
  }

  const updateStep = (idx: number, patch: Partial<WorkflowStep>) =>
    update({ steps: draft.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)) })

  const removeStep = (idx: number) =>
    update({ steps: draft.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, stepNo: i + 1 })) })

  const handleSave = async () => {
    if (draft.isActive && !draft.steps.length) {
      message.error(`Add at least one approval step before enabling the ${draft.module} workflow.`)
      return
    }
    const unnamed = draft.steps.findIndex(s => !s.stepName.trim())
    if (unnamed !== -1) {
      message.error(`Step ${unnamed + 1} needs a name.`)
      return
    }
    const noApprover = draft.steps.findIndex(s => !s.approverName)
    if (draft.isActive && noApprover !== -1) {
      message.error(`"${draft.steps[noApprover].stepName}" needs an approver.`)
      return
    }
    setSaving(true)
    try {
      await onSave(draft)
      message.success(`${draft.module} workflow saved`)
    } catch { /* error already surfaced by the context */ }
    setSaving(false)
  }

  return (
    <Card
      size="small"
      style={{
        borderRadius: 10,
        border: draft.isActive ? '1.5px solid #2563EB' : '1.5px solid #E4E8F0',
        background: draft.isActive ? '#F8FAFF' : '#FAFAFA',
      }}
      bodyStyle={{ padding: '14px 18px' }}
    >
      {/* Module header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: draft.isActive ? 14 : 0, gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <ApartmentOutlined style={{ color: draft.isActive ? '#2563EB' : '#8A93A6', fontSize: 16 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: draft.isActive ? '#0B1426' : '#8A93A6' }}>
              {draft.module}
              {draft.module === 'Mapping' && (
                <Tag color="purple" style={{ marginLeft: 8, fontSize: 10 }}>Admin approves</Tag>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#8A93A6' }}>
              {draft.steps.length} approval step{draft.steps.length !== 1 ? 's' : ''} · {MODULE_HINT[draft.module] || ''}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Enable</Text>
          <Switch size="small" checked={draft.isActive} onChange={v => update({ isActive: v })} />
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            loading={saving}
            disabled={!dirty}
            onClick={handleSave}
          >
            {dirty ? 'Save' : 'Saved'}
          </Button>
        </div>
      </div>

      {draft.isActive && (
        <div>
          {draft.steps.length === 0 ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approval steps yet" style={{ margin: '8px 0' }} />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
              {draft.steps.map((step, idx) => (
                <div
                  key={idx}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '36px 1fr 1fr 130px 80px 32px',
                    gap: 8, alignItems: 'center',
                    background: '#fff', borderRadius: 8, padding: '8px 12px',
                    border: '1px solid #E4E8F0',
                  }}
                >
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    background: '#2563EB', color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 12, flexShrink: 0,
                  }}>
                    {step.stepNo}
                  </div>

                  <Input
                    size="small"
                    value={step.stepName}
                    onChange={e => updateStep(idx, { stepName: e.target.value })}
                    placeholder={defaultStepName(draft.module, idx)}
                    style={{ fontWeight: 500 }}
                  />

                  <Select
                    size="small"
                    value={step.approverName || undefined}
                    onChange={v => updateStep(idx, { approverName: v })}
                    placeholder="Approver Employee"
                    options={employeeOptions}
                    showSearch
                    optionFilterProp="label"
                    style={{ width: '100%' }}
                  />

                  <Select
                    size="small"
                    value={step.action}
                    onChange={v => updateStep(idx, { action: v })}
                    style={{ width: '100%' }}
                    options={[
                      { value: 'approve', label: '✅ Approve Only' },
                      { value: 'reject',  label: '❌ Reject Only' },
                      { value: 'both',    label: '⚡ Both' },
                    ]}
                  />

                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Switch size="small" checked={step.isRequired} onChange={v => updateStep(idx, { isRequired: v })} />
                    <Text style={{ fontSize: 11, color: '#8A93A6' }}>Req.</Text>
                  </div>

                  <Tooltip title="Remove step">
                    <Button type="text" size="small" danger icon={<MinusCircleOutlined />} onClick={() => removeStep(idx)} />
                  </Tooltip>
                </div>
              ))}
            </div>
          )}

          <Button
            type="dashed"
            size="small"
            icon={<PlusOutlined />}
            onClick={addStep}
            style={{ width: '100%', borderColor: '#2563EB', color: '#2563EB' }}
          >
            Add Approval Step
          </Button>
        </div>
      )}
    </Card>
  )
}

// ── Page ────────────────────────────────────────────────────────────────────
const WorkflowMaster: React.FC = () => {
  const { workflows, employees, saveWorkflow, masterDataLoaded } = useMasterData()

  // One card per known module, backed by its saved row when there is one.
  const configs: WorkflowConfig[] = WORKFLOW_MODULES.map(mod => {
    const saved = workflows.find(w => w.module && w.module.toLowerCase() === mod.toLowerCase() && w.status !== 'inactive')
    return saved ? { ...saved, steps: saved.steps || [] } : blankConfig(mod)
  })

  const handleSave = async (cfg: WorkflowConfig) => {
    const existing = workflows.find(w => w.module && w.module.toLowerCase() === cfg.module.toLowerCase() && w.status !== 'inactive')
    const id = cfg.id || existing?.id || `WF${cfg.module.slice(0, 6).toUpperCase()}`
    const isEdit = !!(cfg.id || existing?.id)
    const payload = { ...cfg, id, status: 'active' }
    await saveWorkflow(payload, isEdit)
  }

  const activeCount = configs.filter(c => c.isActive).length
  const totalSteps = configs.reduce((n, c) => n + c.steps.length, 0)

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 className="page-title" style={{ marginBottom: 4 }}>Workflow Master</h2>
        <p className="page-subtitle">
          Approval chains for each business module — who signs off, in what order, and what they may do.
        </p>
      </div>

      <KpiCardRow
        colProps={{ xs: 24, sm: 8 }}
        cards={[
          { label: 'Modules', value: configs.length, icon: <ApartmentOutlined style={{ fontSize: 24, color: '#2563EB' }} />, iconBg: 'rgba(37,99,235,0.08)', borderColor: '#2563EB' },
          { label: 'Workflows Enabled', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Approval Steps', value: totalSteps, icon: <PlusOutlined style={{ fontSize: 24, color: '#D97706' }} />, iconBg: 'rgba(217,119,6,0.08)', borderColor: '#D97706' },
        ]}
      />

      {!masterDataLoaded ? (
        <TableSkeleton rows={5} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 16 }}>
          {configs.map(cfg => (
            <WorkflowCard
              key={cfg.module}
              config={cfg}
              employees={employees}
              onSave={handleSave}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default WorkflowMaster
